import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { ShieldCheck, RefreshCw, PlayCircle } from 'lucide-react';
import KakoAuthModal from '../KakoAuthModal';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const authHeaders = () => ({
  Authorization: `Bearer ${localStorage.getItem('access_token') || sessionStorage.getItem('access_token')}`,
});

const fmtDate = (iso) => {
  try {
    return new Date(iso).toLocaleString('pt-BR', { timeZone: 'America/Sao_Paulo' });
  } catch {
    return iso || '-';
  }
};

const KakoAuthManagement = () => {
  const [modalOpen, setModalOpen] = useState(false);
  const [lastResult, setLastResult] = useState(null);
  const [activity, setActivity] = useState({ challenges: [], tokens: [] });
  const [loading, setLoading] = useState(false);

  const loadActivity = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${BACKEND_URL}/api/kako-auth/admin/activity`,
        { headers: authHeaders() });
      setActivity(res.data);
    } catch (e) {
      console.error('Erro ao carregar atividade OTP', e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadActivity(); }, [loadActivity]);

  const statusBadge = (c) => {
    const expired = c.expires_at && new Date(c.expires_at) < new Date();
    if (c.used) return <Badge className="bg-green-100 text-green-700">✅ Validado</Badge>;
    if (expired) return <Badge className="bg-gray-100 text-gray-600">⏰ Expirado</Badge>;
    if (c.attempts >= 5) return <Badge className="bg-red-100 text-red-700">🚫 Bloqueado</Badge>;
    return <Badge className="bg-amber-100 text-amber-700">⏳ Pendente</Badge>;
  };

  return (
    <div className="space-y-4" data-testid="kako-auth-management">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <ShieldCheck className="w-5 h-5 text-orange-500" />
            Autenticador KAKO (código via chat)
          </CardTitle>
          <CardDescription>
            Módulo de verificação de posse da conta: envia um código de 6 dígitos por DM
            no app KAKO e valida no site. Reutilizável em qualquer fluxo futuro
            (cadastro, votação, eventos) — cada fluxo usa sua própria finalidade (purpose).
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="flex flex-wrap gap-2">
            <Button
              data-testid="kako-auth-test-button"
              onClick={() => { setLastResult(null); setModalOpen(true); }}
              className="bg-orange-500 hover:bg-orange-600"
            >
              <PlayCircle className="w-4 h-4 mr-2" />
              Testar autenticador
            </Button>
            <Button variant="outline" onClick={loadActivity} disabled={loading}
              data-testid="kako-auth-refresh-button">
              <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
              Atualizar
            </Button>
          </div>
          {lastResult && (
            <div className="bg-green-50 border border-green-200 rounded-lg p-3 text-sm"
              data-testid="kako-auth-test-result">
              ✅ Verificação concluída para o KAKO ID <b>{lastResult.kakoId}</b>.
              Token emitido: <code className="bg-white px-1 rounded">{lastResult.token.slice(0, 8)}…</code>
              {' '}(válido por 15 min, finalidade <b>admin_teste</b>)
            </div>
          )}
          <div className="text-xs text-gray-500 space-y-1">
            <p>🔒 Códigos: 6 dígitos, validade 5 min, máx. 5 tentativas, hash no banco (nunca texto puro).</p>
            <p>🛡️ Limites: 3 códigos/hora por KAKO ID, cooldown de 60s entre reenvios, limites por IP.</p>
            <p>🤖 O envio usa o mesmo canal das notificações de crédito — sem interferir no bot de vendas.</p>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Atividade recente</CardTitle>
        </CardHeader>
        <CardContent>
          {activity.challenges.length === 0 ? (
            <p className="text-sm text-gray-500" data-testid="kako-auth-no-activity">
              Nenhuma verificação registrada ainda.
            </p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="kako-auth-activity-table">
                <thead>
                  <tr className="text-left text-gray-500 border-b">
                    <th className="py-2 pr-3">KAKO ID</th>
                    <th className="py-2 pr-3">Finalidade</th>
                    <th className="py-2 pr-3">Status</th>
                    <th className="py-2 pr-3">Tentativas</th>
                    <th className="py-2 pr-3">Solicitado em</th>
                  </tr>
                </thead>
                <tbody>
                  {activity.challenges.map((c) => (
                    <tr key={c.id} className="border-b last:border-0">
                      <td className="py-2 pr-3 font-mono">{c.kako_id}</td>
                      <td className="py-2 pr-3">{c.purpose}</td>
                      <td className="py-2 pr-3">{statusBadge(c)}</td>
                      <td className="py-2 pr-3">{c.attempts}/5</td>
                      <td className="py-2 pr-3">{fmtDate(c.created_at)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      <KakoAuthModal
        open={modalOpen}
        onOpenChange={setModalOpen}
        purpose="admin_teste"
        title="Teste do Autenticador"
        onVerified={(token, kakoId) => {
          setLastResult({ token, kakoId });
          loadActivity();
        }}
      />
    </div>
  );
};

export default KakoAuthManagement;
